import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, X, MapPin, Gauge, Calendar, User, ChevronDown, Heart } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { api, INR, INR_SHORT } from "@/lib/marketplace";
import { useAuth } from "@/lib/auth";
import { useFavourites } from "@/lib/favourites";
import { BIKE_BRANDS, INDIAN_STATES } from "@/data/formOptions";

const SORTS = [
  { v: "newest", l: "Newly Listed" },
  { v: "price_asc", l: "Price: Low → High" },
  { v: "price_desc", l: "Price: High → Low" },
  { v: "year_desc", l: "Year: Newest" },
  { v: "km_asc", l: "KM: Lowest" },
];

export default function Marketplace() {
  const [filters, setFilters] = useState({
    q: "", brand: "", state: "", city: "", price_min: "", price_max: "", year_min: "",
    km_max: "", condition: "", sort: "newest",
  });
  const [facets, setFacets] = useState({ brands: [], states: [], cities: [] });
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 24 });
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => { document.title = "Marketplace — Biker Boulevard"; window.scrollTo(0, 0); }, []);
  useEffect(() => { api.getFacets().then(setFacets).catch(() => {}); }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = {};
        Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
        params.page = 1;
        const res = await api.listListings(params);
        setData(res);
      } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [filters]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearAll = () => setFilters({ q: "", brand: "", state: "", city: "", price_min: "", price_max: "", year_min: "", km_max: "", condition: "", sort: "newest" });
  const activeFilterCount = useMemo(() => Object.entries(filters).filter(([k, v]) => k !== "sort" && v !== "").length, [filters]);

  return (
    <main data-testid="marketplace-page" className="relative bg-[#0D0F12] text-white min-h-screen">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,204,204,0.06), transparent 50%), #0D0F12" }} />
        <div className="absolute inset-0 grid-bg opacity-10" />
      </div>

      <div className="pt-28 pb-20 max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Marketplace</span>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
              Explore vehicles listed by sellers on <span className="text-gradient-cyan">Biker Boulevard</span>
            </h1>
            <p className="mt-3 text-sm sm:text-base text-zinc-400 max-w-2xl leading-relaxed">
              Browse verified pre-owned motorcycles from sellers across India — compare prices, mileage, year and condition, save your favourites, and chat directly with the owner before you ride.
            </p>
          </div>
        </div>

        {/* Search bar + sort */}
        <div className="flex flex-col lg:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={filters.q} onChange={(e) => setF("q", e.target.value)}
              placeholder="Search by brand, model, variant..." data-testid="search-input"
              className="w-full pl-11 pr-4 py-3 bg-[#15181C] border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#00cccc]/50" />
          </div>
          <button onClick={() => setDrawer(true)} className="lg:hidden inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-white/10 text-white" data-testid="open-filters">
            <SlidersHorizontal className="w-4 h-4" /> Filters {activeFilterCount > 0 && <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-[#00cccc] text-black font-mono">{activeFilterCount}</span>}
          </button>
          <div className="relative">
            <select value={filters.sort} onChange={(e) => setF("sort", e.target.value)}
              className="appearance-none pl-4 pr-10 py-3 bg-[#15181C] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#00cccc]/50"
              style={{ colorScheme: "dark" }} data-testid="sort-select">
              {SORTS.map((s) => <option key={s.v} value={s.v} className="bg-[#15181C]">{s.l}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          </div>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
          {/* Filters sidebar — desktop */}
          <aside className="hidden lg:block">
            <FilterPanel filters={filters} setF={setF} clearAll={clearAll} facets={facets} />
          </aside>

          {/* Mobile drawer */}
          {drawer && (
            <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="filters-drawer">
              <div className="flex-1 bg-black/60" onClick={() => setDrawer(false)} />
              <div className="w-[85vw] max-w-sm bg-[#1A1E23] border-l border-white/10 p-5 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Filters</h3>
                  <button onClick={() => setDrawer(false)}><X className="w-5 h-5" /></button>
                </div>
                <FilterPanel filters={filters} setF={setF} clearAll={clearAll} facets={facets} />
              </div>
            </div>
          )}

          {/* Results grid */}
          <div>
            {loading && data.items.length === 0 && (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[4/3] rounded-2xl bg-white/[0.04] border border-white/10 animate-pulse" />
                ))}
              </div>
            )}
            {!loading && data.items.length === 0 && (
              <div className="p-10 rounded-2xl border border-white/10 bg-white/[0.03] text-center" data-testid="no-results">
                <p className="text-lg font-semibold text-white">No motorcycles match your filters</p>
                <p className="mt-2 text-sm text-zinc-400">Be the first to list — tap &quot;List My Bike&quot; in the nav.</p>
                <button onClick={clearAll} className="mt-5 px-5 py-2.5 rounded-full border border-[#00cccc]/40 text-[#00cccc] hover:bg-[#00cccc] hover:text-black transition-all text-sm">Clear filters</button>
              </div>
            )}
            {data.items.length > 0 && (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {data.items.map((it) => <ListingCard key={it.listing_id} item={it} />)}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}

function FilterPanel({ filters, setF, clearAll, facets }) {
  return (
    <div className="space-y-5 p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Filters</h3>
        <button onClick={clearAll} className="text-xs text-zinc-500 hover:text-[#00cccc]">Clear all</button>
      </div>
      <FilterSelect label="Brand" value={filters.brand} onChange={(v) => setF("brand", v)} options={["", ...BIKE_BRANDS]} />
      <FilterSelect label="State" value={filters.state} onChange={(v) => setF("state", v)} options={["", ...INDIAN_STATES]} />
      <FilterCity label="City" value={filters.city} onChange={(v) => setF("city", v)} options={(facets.cities || []).map((c) => c.name)} />
      <div className="grid grid-cols-2 gap-2">
        <FilterNumber label="Min ₹" value={filters.price_min} onChange={(v) => setF("price_min", v)} />
        <FilterNumber label="Max ₹" value={filters.price_max} onChange={(v) => setF("price_max", v)} />
      </div>
      <FilterNumber label="Year from" value={filters.year_min} onChange={(v) => setF("year_min", v)} />
      <FilterNumber label="Max KMs" value={filters.km_max} onChange={(v) => setF("km_max", v)} />
      <FilterSelect label="Condition" value={filters.condition} onChange={(v) => setF("condition", v)}
        options={["", "Showroom Condition", "Very Good", "Good", "Average", "Needs Work"]} />
    </div>
  );
}
function FilterSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={`filter-${label.toLowerCase()}`}
        className="w-full px-3 py-2 bg-[#15181C] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#00cccc]/50"
        style={{ colorScheme: "dark" }}>
        {options.map((o) => <option key={o} value={o} className="bg-[#15181C]">{o || "Any"}</option>)}
      </select>
    </div>
  );
}
function FilterNumber({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} type="number"
        data-testid={`filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="w-full px-3 py-2 bg-[#15181C] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#00cccc]/50" />
    </div>
  );
}

function FilterCity({ label, value, onChange, options }) {
  const listId = "city-facets-datalist";
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        list={listId}
        placeholder="e.g. Lucknow"
        data-testid="filter-city"
        className="w-full px-3 py-2 bg-[#15181C] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#00cccc]/50"
      />
      <datalist id={listId}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
    </div>
  );
}

export function ListingCard({ item, compact = false }) {
  const cover = item.photo_ids?.[item.primary_photo_index] || item.photo_ids?.[0];
  const img = cover ? api.photoUrl(cover) : null;
  const { user, login } = useAuth() || {};
  const favApi = useFavourites();
  const isFav = !!favApi?.favs?.has(item.listing_id);
  const onFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { login?.(); return; }
    favApi?.toggleFav(item.listing_id);
  };
  return (
    <Link to={`/marketplace/${item.listing_id}`} data-testid={`listing-card-${item.listing_id}`}
      className={`group overflow-hidden border border-white/10 bg-white/[0.03] hover:border-[#00cccc]/40 hover:bg-white/[0.04] transition-all ${compact ? "rounded-xl" : "rounded-2xl"}`}>
      <div className={`${compact ? "aspect-[16/10]" : "aspect-[4/3]"} bg-[#1A1E23] relative overflow-hidden`}>
        {img ? (
          <img src={img} alt={item.model} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs">No photo</div>
        )}
        {item.verified && (
          <span className={`absolute rounded-full font-mono bg-[#00cccc] text-black font-semibold ${compact ? "top-2 left-2 px-1.5 py-0.5 text-[8px]" : "top-3 left-3 px-2 py-0.5 text-[9px]"}`}>BB VERIFIED</span>
        )}
        <button onClick={onFav} data-testid={`fav-toggle-${item.listing_id}`} aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
          className={`absolute rounded-full flex items-center justify-center backdrop-blur-md transition-all ${compact ? "top-2 right-2 w-7 h-7" : "top-3 right-3 w-9 h-9"} ${isFav ? "bg-red-500/15 border border-red-500/60" : "bg-black/40 border border-white/15 hover:border-red-400/60"}`}>
          <Heart className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} transition-colors ${isFav ? "text-red-500 fill-red-500" : "text-white/80"}`} />
        </button>
      </div>
      <div className={compact ? "p-3" : "p-4"}>
        <div className={`${compact ? "text-[10px]" : "text-[11px]"} font-sans font-semibold text-zinc-300 uppercase tracking-wider`}>{item.registration_year} · {item.brand}</div>
        <h3 className={`mt-1 ${compact ? "text-sm" : "text-base"} font-semibold text-white truncate`}>{item.brand} {item.model} {item.variant || ""}</h3>
        <div className={`mt-1 ${compact ? "text-base" : "text-xl"} font-bold text-[#00cccc]`}>{INR(item.expected_price)}</div>
        <div className={`${compact ? "mt-2 gap-2 text-[10px]" : "mt-3 gap-3 text-[11px]"} flex items-center text-zinc-400`}>
          <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{item.kms_driven?.toLocaleString("en-IN")} km</span>
          <span className="flex items-center gap-1"><User className="w-3 h-3" />{item.previous_owners} owner</span>
          {item.registration_city && (
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.registration_city}</span>
          )}
        </div>
        {item.valuation && (
          <div className={`${compact ? "mt-2 pt-2" : "mt-3 pt-3"} border-t border-white/10 text-xs font-sans font-medium text-zinc-400`}>
            BB Estimate · {INR_SHORT(item.valuation.low)}–{INR_SHORT(item.valuation.high)}
          </div>
        )}
      </div>
    </Link>
  );
}
